/**
 * /app/$tenantSlug/settings/attendance — Settings → Time & Leave →
 * Attendance & Geofence (HR-admin).
 *
 * Office geofence anchors (CRUD) + the tenant attendance toggles: selfie
 * required (off by default), geofence strict, attendance drives payroll LOP,
 * and the default weekly-off day. All server-validated; the page is HR-admin
 * (the backend re-checks every write).
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useLocations,
  useCreateLocation,
  useDeleteLocation,
  useAttendanceSettings,
  useUpdateAttendanceSettings,
} from "@/hooks/use-attendance";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/attendance",
)({
  component: SettingsAttendanceRoute,
});

function SettingsAttendanceRoute() {
  const settings = useAttendanceSettings();
  const updateSettings = useUpdateAttendanceSettings();
  const locations = useLocations();
  const createLoc = useCreateLocation();
  const deleteLoc = useDeleteLocation();

  const [name, setName] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState("200");

  async function addLocation() {
    const latitude = Number(lat);
    const longitude = Number(lng);
    if (!name.trim() || Number.isNaN(latitude) || Number.isNaN(longitude)) {
      toast.error("Name, latitude and longitude are required");
      return;
    }
    try {
      await createLoc.mutateAsync({
        name: name.trim(),
        latitude,
        longitude,
        radius_metres: Number(radius) || 200,
      });
      toast.success("Office location added");
      setName("");
      setLat("");
      setLng("");
      setRadius("200");
    } catch {
      toast.error("Failed to add location");
    }
  }

  async function toggle(
    field: "selfie_required" | "geofence_strict" | "drives_payroll_lop",
    value: boolean,
  ) {
    try {
      await updateSettings.mutateAsync({ [field]: value });
    } catch {
      toast.error("Failed to update setting");
    }
  }

  const s = settings.data;

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Attendance &amp; Geofence</h2>
            <p className="text-muted-foreground text-sm">
              Configure punch capture and office geofence anchors.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Capture policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={s?.selfie_required ?? false}
                  onCheckedChange={(c) => toggle("selfie_required", c === true)}
                />
                Require a selfie at punch time
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={s?.geofence_strict ?? false}
                  onCheckedChange={(c) => toggle("geofence_strict", c === true)}
                />
                Strict geofence (reserved — punches are accept-but-flag in v1)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={s?.drives_payroll_lop ?? false}
                  onCheckedChange={(c) => toggle("drives_payroll_lop", c === true)}
                />
                Attendance drives payroll loss-of-pay days
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Office locations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid items-end gap-3 sm:grid-cols-5">
                <div className="sm:col-span-2">
                  <Label htmlFor="ln">Name</Label>
                  <Input id="ln" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="lat">Latitude</Label>
                  <Input id="lat" value={lat} onChange={(e) => setLat(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="lng">Longitude</Label>
                  <Input id="lng" value={lng} onChange={(e) => setLng(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="rad">Radius (m)</Label>
                  <Input id="rad" value={radius} onChange={(e) => setRadius(e.target.value)} />
                </div>
              </div>
              <Button onClick={addLocation} disabled={createLoc.isPending}>
                Add location
              </Button>

              <ul className="divide-y text-sm">
                {(locations.data?.locations ?? []).map((l) => (
                  <li key={l.id} className="flex items-center justify-between py-2">
                    <span>
                      {l.name} — {l.latitude}, {l.longitude} ({l.radius_metres} m)
                      {!l.is_active && " (inactive)"}
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => deleteLoc.mutate(l.id)}>
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </SettingsShell>
    </>
  );
}
